/**
 * @module components/common/useTopNDraftFields
 * @description TopN 字段读写（count / dynamicState / showOthers）— X 轴 / 条形图 Y 轴共用
 *
 * count 在 base/draft 未落库时，条形图等调用方可传入更大的 defaultCount（如 20）；
 * 已落库后统一走 Schema 读取。
 */
import { useCallback } from 'react';
import {
  useChartSettingsSelector,
  usePatchDraft,
  useSettingState,
} from '../../../context';
import {
  DEFAULT_TOPN_COUNT,
  DEFAULT_TOPN_SHOW_OTHERS,
} from '../../../utils/constants';
import type { ITopNFieldGroupValue } from '.';

/** 从 draft/base 读取 TopN 并提供更新方法 */
export function useTopNDraftFields(
  defaultCount = DEFAULT_TOPN_COUNT,
  defaultDynamicState = false,
): ITopNFieldGroupValue {
  const patchDraft = usePatchDraft();
  const { value: dynamicState, setValue: setDynamicState } =
    useSettingState('dynamicState');
  const { value: showOthers, setValue: setShowOthers } =
    useSettingState('showOthers');
  const { value: schemaCount, setValue: setCount } = useSettingState('count');

  const hasStoredCount = useChartSettingsSelector(
    s => 'count' in s.draft || 'count' in s.base,
  );
  const hasStoredDynamicState = useChartSettingsSelector(
    s => 'dynamicState' in s.draft || 'dynamicState' in s.base,
  );

  const count = hasStoredCount ? schemaCount : defaultCount;
  const resolvedDynamicState = hasStoredDynamicState
    ? dynamicState
    : defaultDynamicState;

  const onCountChange = useCallback(
    (value: number) => {
      if (resolvedDynamicState) {
        patchDraft({
          count: value,
          dynamicState: true,
          showOthers,
        });
        return;
      }
      setCount(value);
    },
    [patchDraft, resolvedDynamicState, setCount, showOthers],
  );

  const onDynamicStateChange = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        patchDraft({
          dynamicState: true,
          count,
          showOthers,
        });
        return;
      }
      setDynamicState(false);
    },
    [count, patchDraft, setDynamicState, showOthers],
  );

  const onShowOthersChange = useCallback(
    (value: string) => {
      if (resolvedDynamicState) {
        patchDraft({
          showOthers: value,
          dynamicState: true,
          count,
        });
        return;
      }
      setShowOthers(value);
    },
    [count, patchDraft, resolvedDynamicState, setShowOthers],
  );

  return {
    count,
    dynamicState: resolvedDynamicState,
    showOthers,
    onCountChange,
    onDynamicStateChange,
    onShowOthersChange,
  };
}
